"use client";

/**
 * Free 5 x 5 risk matrix calculator (/free/risk-matrix-calculator, SEO plan
 * Workstream E row 7).
 *
 * Runs entirely in the browser: the scores live in React state only. Nothing
 * is fetched, stored or put in the URL, and the only analytics are the fixed
 * values in ./analytics.ts. The scale, bands and every word of the result are
 * lib/marketing/risk-matrix/matrix.ts.
 */

import { useId, useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import {
  LEVELS,
  LIKELIHOOD_LABELS,
  NOT_LEGAL_ADVICE,
  SEVERITY_LABELS,
  matrixRows,
  rateRisk,
  type Level,
  type RatedRisk,
} from "@/lib/marketing/risk-matrix/matrix";
import { RISK_ASSESSMENT_MATRIX_ANCHOR } from "@/lib/marketing/risk-matrix/pages";
import { INPUT_CLASS, PRIVATE_REGION_CLASS, SECONDARY_BUTTON_CLASS } from "@/components/marketing/exposure-calculators/fields";
import { trackRiskMatrix } from "./analytics";
import { BAND_CLASSES } from "./band-classes";


type Pick = { likelihood?: Level; severity?: Level };

function LevelSelect({
  id,
  label,
  labels,
  value,
  onChange,
}: {
  id: string;
  label: string;
  labels: Record<Level, string>;
  value: Level | undefined;
  onChange: (value: Level) => void;
}) {
  return (
    <div>
      <label htmlFor={id} className="text-sm font-semibold text-gray-900">
        {label}
      </label>
      <select
        id={id}
        value={value ?? ""}
        onChange={(event) => onChange(Number(event.target.value) as Level)}
        className={INPUT_CLASS}
      >
        <option value="" disabled>
          Choose a score
        </option>
        {LEVELS.map((level) => (
          <option key={level} value={level}>
            {level}: {labels[level]}
          </option>
        ))}
      </select>
    </div>
  );
}

function RatingLine({ title, rated, testId }: { title: string; rated: RatedRisk; testId: string }) {
  return (
    <div data-testid={testId} data-band={rated.band.id} className="rounded-lg border border-gray-200 p-3">
      <p className="text-sm text-gray-600">{title}</p>
      <p className="mt-1 text-sm text-gray-800">
        Likelihood {rated.likelihood} &times; severity {rated.severity} ={" "}
        <span className="text-2xl font-extrabold text-gray-900">{rated.score}</span>
      </p>
      <p className="mt-2">
        <span className={`inline-block rounded-md px-2 py-0.5 text-sm font-bold ${BAND_CLASSES[rated.band.id]}`}>
          {rated.band.label} ({rated.band.range})
        </span>
      </p>
      <p className="mt-2 text-sm leading-relaxed text-gray-700">{rated.band.action}</p>
    </div>
  );
}

export function RiskMatrixCalculator() {
  const posthog = usePostHog();
  const baseId = useId();
  const usedReported = useRef(false);
  const completedReported = useRef(false);
  const [before, setBefore] = useState<Pick>({});
  const [withAfter, setWithAfter] = useState(false);
  const [after, setAfter] = useState<Pick>({});

  const beforeDone = before.likelihood !== undefined && before.severity !== undefined;
  const afterDone = withAfter && after.likelihood !== undefined && after.severity !== undefined;
  const result = beforeDone
    ? rateRisk(
        { likelihood: before.likelihood!, severity: before.severity! },
        afterDone ? { likelihood: after.likelihood!, severity: after.severity! } : null,
      )
    : null;

  function touched(nextBeforeDone: boolean) {
    if (!usedReported.current) {
      usedReported.current = true;
      trackRiskMatrix(posthog, { event: "risk_matrix_used" });
    }
    if (nextBeforeDone && !completedReported.current) {
      completedReported.current = true;
      trackRiskMatrix(posthog, { event: "risk_matrix_completed" });
    }
  }

  function setBeforeField(field: keyof Pick, value: Level) {
    const next = { ...before, [field]: value };
    setBefore(next);
    touched(next.likelihood !== undefined && next.severity !== undefined);
  }

  function setAfterField(field: keyof Pick, value: Level) {
    setAfter({ ...after, [field]: value });
    touched(beforeDone);
  }

  const highlight = (likelihood: Level, severity: Level) => {
    const marks: string[] = [];
    if (result && result.before.likelihood === likelihood && result.before.severity === severity) marks.push("Before");
    if (result?.after && result.after.likelihood === likelihood && result.after.severity === severity) marks.push("After");
    return marks;
  };

  return (
    <section
      aria-labelledby={`${baseId}-heading`}
      data-testid="risk-matrix-calculator"
      data-clarity-mask="true"
      className={`${PRIVATE_REGION_CLASS} rounded-2xl border border-gray-200 bg-gray-50 p-5 sm:p-6`}
    >
      <h2 id={`${baseId}-heading`} className="text-2xl font-bold text-gray-900">
        Score a hazard
      </h2>
      <p className="mt-2 text-sm leading-relaxed text-gray-700">
        Pick how likely it is that the hazard causes harm, and how severe the harm could be. Your scores stay in your
        browser. Nothing you choose is sent to us.
      </p>

      <div className="mt-5 space-y-4 rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="text-base font-bold text-gray-900">Before controls</h3>
        <div className="grid gap-4 sm:grid-cols-2">
          <LevelSelect
            id={`${baseId}-before-likelihood`}
            label="Likelihood"
            labels={LIKELIHOOD_LABELS}
            value={before.likelihood}
            onChange={(value) => setBeforeField("likelihood", value)}
          />
          <LevelSelect
            id={`${baseId}-before-severity`}
            label="Severity"
            labels={SEVERITY_LABELS}
            value={before.severity}
            onChange={(value) => setBeforeField("severity", value)}
          />
        </div>
        <label className="flex items-start gap-3 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={withAfter}
            onChange={(event) => setWithAfter(event.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-gray-300 text-accent focus:ring-accent"
          />
          <span>Add a rating after controls</span>
        </label>
        {withAfter ? (
          <div data-testid="risk-after-fields">
            <h3 className="text-base font-bold text-gray-900">After controls</h3>
            <div className="mt-2 grid gap-4 sm:grid-cols-2">
              <LevelSelect
                id={`${baseId}-after-likelihood`}
                label="Likelihood after controls"
                labels={LIKELIHOOD_LABELS}
                value={after.likelihood}
                onChange={(value) => setAfterField("likelihood", value)}
              />
              <LevelSelect
                id={`${baseId}-after-severity`}
                label="Severity after controls"
                labels={SEVERITY_LABELS}
                value={after.severity}
                onChange={(value) => setAfterField("severity", value)}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className="mt-4">
        <button
          type="button"
          onClick={() => {
            setBefore({});
            setAfter({});
            setWithAfter(false);
          }}
          className={SECONDARY_BUTTON_CLASS}
        >
          Start again
        </button>
      </div>

      <div aria-live="polite" data-testid="risk-result" className="mt-5 space-y-3 rounded-xl border border-gray-200 bg-white p-4">
        {result === null ? (
          <p className="text-sm text-gray-600">Pick a likelihood and a severity to see the score.</p>
        ) : (
          <>
            <RatingLine title="Risk before controls" rated={result.before} testId="risk-before" />
            {result.after ? <RatingLine title="Risk after controls" rated={result.after} testId="risk-after" /> : null}
            <ul className="list-disc space-y-1.5 pl-5 text-sm leading-relaxed text-gray-700">
              {result.notes.map((note) => (
                <li key={note.id} data-testid={`risk-note-${note.id}`}>
                  {note.text}{" "}
                  {note.cite ? (
                    <a
                      href={note.cite.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap font-semibold text-brand-700 underline hover:text-accent-dark"
                    >
                      ({note.cite.label})
                    </a>
                  ) : null}
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-700">
              The bands and actions are the ones in{" "}
              <a href={RISK_ASSESSMENT_MATRIX_ANCHOR} className="font-semibold text-brand-700 underline hover:text-accent-dark">
                our risk assessment guide
              </a>
              . {NOT_LEGAL_ADVICE}
            </p>
          </>
        )}
      </div>

      <div className="mt-5 overflow-x-auto rounded-xl border border-gray-200 bg-white p-4">
        <table data-testid="risk-matrix-table" className="w-full min-w-[480px] border-separate border-spacing-1 text-center text-sm">
          <caption className="pb-2 text-left text-sm font-semibold text-gray-900">
            5 &times; 5 risk matrix: likelihood &times; severity = score
          </caption>
          <thead>
            <tr>
              <th scope="col" className="text-left text-xs font-semibold text-gray-600">
                Severity \ Likelihood
              </th>
              {LEVELS.map((likelihood) => (
                <th key={likelihood} scope="col" className="text-xs font-semibold text-gray-600">
                  {likelihood}. {LIKELIHOOD_LABELS[likelihood]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrixRows().map((row) => (
              <tr key={row.severity}>
                <th scope="row" className="text-left text-xs font-semibold text-gray-600">
                  {row.severity}. {SEVERITY_LABELS[row.severity]}
                </th>
                {row.cells.map((cell) => {
                  const marks = highlight(cell.likelihood, row.severity);
                  return (
                    <td
                      key={cell.likelihood}
                      data-testid={`risk-cell-${cell.likelihood}-${row.severity}`}
                      data-marked={marks.join(" ") || undefined}
                      className={`rounded-md px-2 py-2 font-bold ${BAND_CLASSES[cell.band]} ${marks.length ? "ring-2 ring-gray-900" : ""}`}
                    >
                      {cell.score}
                      {marks.length ? <span className="block text-xs font-semibold">{marks.join(" and ")}</span> : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
